/**
 * Exact money formatting for reconciliation.
 *
 * ⚠️ Do not reuse `formatAmount` from `lib/utils.ts` for reconciliation amounts.
 * It runs the value through `parseFloat` and caps the output at two fraction
 * digits, so a `decimal(18,4)` amount of `2525.7525` renders as `$2,525.75` and
 * the third and fourth decimals are gone with no warning. Bank and ledger amounts
 * have to be shown exactly as stored.
 *
 * Everything here operates on the decimal string. No value is ever converted to a
 * JavaScript number, so nothing can be lost to binary floating point.
 */

const CURRENCY_SYMBOL: Record<string, string> = { USD: '$' };

/** Groups an integer digit string in threes without going through a number. */
const groupThousands = (digits: string): string => {
  let out = '';
  for (let i = 0; i < digits.length; i += 1) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += ',';
    out += digits[i];
  }
  return out;
};

/**
 * Formats an exact decimal string for display, preserving every digit it carries.
 *
 * A fraction shorter than two digits is padded to two, because money reads wrong
 * at one decimal place. A longer fraction is left alone rather than rounded.
 *
 * @example formatExactAmount('2525.7525') // '2,525.7525'
 * @example formatExactAmount('12500.5')   // '12,500.50'
 */
export const formatExactAmount = (amount: string): string => {
  const trimmed = amount.trim();
  // Anything that is not a plain decimal is returned untouched rather than
  // guessed at, so a malformed value is visible instead of silently becoming 0.
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return amount;

  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [whole, fraction = ''] = unsigned.split('.');
  const padded = fraction.length < 2 ? fraction.padEnd(2, '0') : fraction;
  return `${negative ? '-' : ''}${groupThousands(whole)}.${padded}`;
};

/**
 * Formats an amount with its currency and an explicit direction sign.
 *
 * Amounts arrive positive and carry their direction separately, so the sign is
 * applied here rather than being baked into the stored value.
 *
 * A `null` direction renders unsigned. The bank sent a type code we cannot place,
 * so the amount is known and the direction is not; printing `+` or `-` would state
 * something the data does not say.
 */
export const formatSignedAmount = (
  amount: string,
  direction: 'credit' | 'debit' | null,
  currency = 'USD'
): string => {
  const symbol = CURRENCY_SYMBOL[currency] ?? '';
  if (direction === null) return `${symbol}${formatExactAmount(amount)}`;
  const sign = direction === 'credit' ? '+' : '-';
  return `${sign}${symbol}${formatExactAmount(amount)}`;
};

/**
 * Formats a day's net total, which is already signed in the source data.
 *
 * A total of exactly zero is rendered without a sign, because a quiet day is a
 * result rather than a movement in either direction.
 */
export const formatNetAmount = (amount: string, currency = 'USD'): string => {
  const symbol = CURRENCY_SYMBOL[currency] ?? '';
  const negative = amount.trim().startsWith('-');
  const formatted = formatExactAmount(negative ? amount.trim().slice(1) : amount);
  return `${negative ? '-' : ''}${symbol}${formatted}`;
};

/** True when a decimal string is zero regardless of how it is written. */
export const isZeroAmount = (amount: string): boolean => /^-?0+(\.0+)?$/.test(amount.trim());
